import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash, IconEdit, IconReceipt } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useActuals,
  useDeleteActual,
  type Actual,
} from "@/hooks/useActuals";
import { ActualForm, type ActualFormData } from "@/components/ActualForm";
import { useCreateActual, useUpdateActual } from "@/hooks/useActuals";
import { useProjectCostCodes } from "@/hooks/useCostCodes";

export const Route = createFileRoute("/projects/$projectId/actuals" as any)({
  component: ActualsPage,
});

function ActualsPage() {
  const params = Route.useParams();
  const projectId = "projectId" in params ? params.projectId : "";
  const [costCodeFilter, setCostCodeFilter] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const { data, isLoading } = useActuals(projectId, {
    costCodeId: costCodeFilter || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  const createActual = useCreateActual(projectId);
  const updateActual = useUpdateActual(projectId);
  const deleteActual = useDeleteActual(projectId);
  const { data: projectCostCodesData } = useProjectCostCodes(projectId);

  const [addModalOpened, { open: openAddModal, close: closeAddModal }] = useDisclosure(false);
  const [editModalOpened, { open: openEditModal, close: closeEditModal }] = useDisclosure(false);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [actualToEdit, setActualToEdit] = useState<Actual | null>(null);
  const [actualToDelete, setActualToDelete] = useState<Actual | null>(null);

  const actuals = data?.actuals || [];
  const summary = data?.summary || {
    totalActuals: 0,
    totalAmount: 0,
    byCostCode: [],
  };

  const handleAddActual = async (formData: ActualFormData) => {
    try {
      await createActual.mutateAsync({
        costCodeId: formData.costCodeId,
        amount: formData.amount,
        description: formData.description || undefined,
        date: formData.date,
        commitmentId: formData.commitmentId,
      });
      notifications.show({
        title: "Success",
        message: "Expense added!",
        color: "green",
      });
      closeAddModal();
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to add expense",
        color: "red",
      });
    }
  };

  const handleEditClick = (actual: Actual) => {
    setActualToEdit(actual);
    openEditModal();
  };

  const handleEditActual = async (formData: ActualFormData) => {
    if (!actualToEdit) return;

    try {
      await updateActual.mutateAsync({
        id: actualToEdit.id,
        data: {
          amount: formData.amount,
          description: formData.description || undefined,
          date: formData.date,
          commitmentId: formData.commitmentId,
        },
      });
      notifications.show({
        title: "Success",
        message: "Expense updated!",
        color: "green",
      });
      closeEditModal();
      setActualToEdit(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to update expense",
        color: "red",
      });
    }
  };

  const handleDeleteClick = (actual: Actual) => {
    setActualToDelete(actual);
    openDeleteModal();
  };

  const handleDeleteConfirm = async () => {
    if (!actualToDelete) return;

    try {
      await deleteActual.mutateAsync(actualToDelete.id);
      notifications.show({
        title: "Success",
        message: "Expense deleted!",
        color: "green",
      });
      closeDeleteModal();
      setActualToDelete(null);
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to delete expense",
        color: "red",
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Prepare cost code filter options
  const projectCostCodes = projectCostCodesData?.projectCostCodes ?? [];
  const costCodeFilterOptions = projectCostCodes.map((pcc) => ({
    value: pcc.costCodeId,
    label: `${pcc.costCode?.code ?? "?"} - ${pcc.costCode?.name ?? "Unknown"}`,
  }));

  if (isLoading) {
    return (
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Skeleton height={40} width={200} />
          <Skeleton height={400} radius="md" />
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Paper p="xl" withBorder>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <Title order={2}>Actuals</Title>
            <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
              Add Expense
            </Button>
          </Group>

          {/* Summary Cards */}
          <Group grow>
            <Paper p="md" withBorder style={{ background: "var(--mantine-color-red-0)" }}>
              <Text size="sm" c="dimmed">
                Total Actuals
              </Text>
              <Text size="xl" fw={700}>
                {formatCurrency(summary.totalAmount)}
              </Text>
              <Text size="xs" c="dimmed">
                {summary.totalActuals} expenses
              </Text>
            </Paper>
            <Paper p="md" withBorder>
              <Text size="sm" c="dimmed">
                By Cost Code
              </Text>
              <Stack gap={4} mt="xs">
                {summary.byCostCode.slice(0, 3).map((cc) => (
                  <Group key={cc.costCode} justify="space-between" gap="xs">
                    <Text size="xs" c="dimmed" lineClamp={1}>
                      {cc.costCode}
                    </Text>
                    <Text size="xs" fw={600}>
                      {formatCurrency(cc.total)}
                    </Text>
                  </Group>
                ))}
                {summary.byCostCode.length > 3 && (
                  <Text size="xs" c="dimmed">
                    +{summary.byCostCode.length - 3} more
                  </Text>
                )}
              </Stack>
            </Paper>
          </Group>

          {/* Filters */}
          <Group>
            <Select
              placeholder="Filter by cost code"
              clearable
              value={costCodeFilter}
              onChange={setCostCodeFilter}
              data={costCodeFilterOptions}
              searchable
              style={{ width: 250 }}
            />
            <TextInput
              placeholder="Start date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.currentTarget.value)}
              style={{ width: 150 }}
            />
            <TextInput
              placeholder="End date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.currentTarget.value)}
              style={{ width: 150 }}
            />
          </Group>

          {actuals.length === 0 ? (
            <Paper p="xl" withBorder style={{ borderStyle: "dashed" }}>
              <Stack gap="md" align="center">
                <IconReceipt size={48} stroke={1.5} style={{ opacity: 0.5 }} />
                <Title order={3} c="dimmed">No Expenses Recorded</Title>
                <Text c="dimmed" ta="center" maw={500}>
                  Track actual expenses and invoices to monitor real costs against your budget. Record labor, materials, subcontractor invoices, and other project expenses.
                </Text>
                <Button leftSection={<IconPlus size={16} />} onClick={openAddModal}>
                  Add Your First Expense
                </Button>
                <Text size="xs" c="dimmed" ta="center" maw={600} mt="md">
                  Expenses are automatically compared against your budget and commitments to track cost variance in the Cost Dashboard.
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Cost Code</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                  <Table.Th style={{ width: "100px" }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {actuals.map((actual) => (
                  <Table.Tr key={actual.id}>
                    <Table.Td>
                      <Text size="sm">{formatDate(actual.date)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {actual.costCode}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {actual.costCodeName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {actual.description ? (
                        <Text size="sm" lineClamp={2}>
                          {actual.description}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          No description
                        </Text>
                      )}
                      {actual.commitmentId && (
                        <Badge size="xs" variant="light" color="blue" mt={4}>
                          Linked to commitment
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Text size="sm" fw={600} c="red">
                        {formatCurrency(actual.amount)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-end">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          onClick={() => handleEditClick(actual)}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => handleDeleteClick(actual)}
                          loading={deleteActual.isPending}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      {/* Add Expense Modal */}
      <Modal opened={addModalOpened} onClose={closeAddModal} title="Add Expense" size="lg" centered>
        <ActualForm
          onSubmit={handleAddActual}
          onCancel={closeAddModal}
          loading={createActual.isPending}
          submitText="Save Expense"
          projectId={projectId}
        />
      </Modal>

      {/* Edit Expense Modal */}
      <Modal
        opened={editModalOpened}
        onClose={() => {
          closeEditModal();
          setActualToEdit(null);
        }}
        title="Edit Expense"
        size="lg"
        centered
      >
        {actualToEdit && (
          <ActualForm
            onSubmit={handleEditActual}
            onCancel={() => {
              closeEditModal();
              setActualToEdit(null);
            }}
            initialData={actualToEdit}
            loading={updateActual.isPending}
            submitText="Update Expense"
            projectId={projectId}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Delete Expense" centered>
        <Stack gap="md">
          <Text>Are you sure you want to delete this expense? This action cannot be undone.</Text>
          {actualToDelete && (
            <Paper p="sm" withBorder>
              <Text size="sm" fw={500}>
                {actualToDelete.costCode} - {actualToDelete.costCodeName}
              </Text>
              <Text size="sm" c="dimmed">
                {formatCurrency(actualToDelete.amount)} on {formatDate(actualToDelete.date)}
              </Text>
            </Paper>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDeleteModal} disabled={deleteActual.isPending}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteConfirm} loading={deleteActual.isPending}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
